"use client";

import { useEffect, useSyncExternalStore } from "react";

export const FOLLOWZAP_SHELL_STORAGE_KEY = "bp-followzap:web-beta:shell:v1";
export const FOLLOWZAP_SHELL_QUERY_KEY = "followzap-shell";
export const FOLLOWZAP_SHELL_CHANGE_EVENT = "bp:followzap-shell-change";

export type PreferenciaShellFollowZap = "current" | "experimental";

type ContextoVisualFollowZap = {
  produto?: string | null;
  papel?: string | null;
  desktop: boolean;
  temaEscuro: boolean;
  areaAnaliseGestorFollowZap?: boolean;
  areaGerencialAnalise?: boolean;
};

export function deveUsarPaletaDarkFollowZap({
  produto,
  papel,
  desktop,
  temaEscuro,
  areaAnaliseGestorFollowZap,
  areaGerencialAnalise,
}: ContextoVisualFollowZap) {
  return ((produto === "FOLLOW_ZAP" && (papel === "USUARIO" || (papel === "GESTOR" && areaAnaliseGestorFollowZap === true))) ||
      (produto === "FOLLOW_PRO" && (papel === "USUARIO" || (papel === "GESTOR" && areaGerencialAnalise === true)))) &&
    desktop && temaEscuro;
}

export function deveUsarShellExperimentalFollowZap({
  preferencia,
  ...contexto
}: ContextoVisualFollowZap & {
  preferencia: PreferenciaShellFollowZap;
}) {
  return deveUsarPaletaDarkFollowZap(contexto) &&
    !(contexto.produto === "FOLLOW_ZAP" && contexto.papel === "GESTOR") && preferencia === "experimental";
}

function temaEscuroNoNavegador() {
  return document.documentElement.dataset.theme === "dark";
}

function temaEscuroNoServidor() {
  return false;
}

function assinarTemaFollowZap(aoMudar: () => void) {
  const observer = new MutationObserver(aoMudar);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

export function normalizarPreferenciaShellFollowZap(valor: unknown): PreferenciaShellFollowZap {
  return valor === "experimental" ? "experimental" : "current";
}

export function lerPreferenciaShellFollowZap(storage: Pick<Storage, "getItem">) {
  try {
    return normalizarPreferenciaShellFollowZap(storage.getItem(FOLLOWZAP_SHELL_STORAGE_KEY));
  } catch {
    return "current" as const;
  }
}

function lerPreferenciaShellNoNavegador() {
  return lerPreferenciaShellFollowZap(window.localStorage);
}

function preferenciaShellNoServidor() {
  return "current" as const;
}

function assinarPreferenciaShellFollowZap(aoMudar: () => void) {
  const aoStorage = (evento: StorageEvent) => {
    if (evento.key === FOLLOWZAP_SHELL_STORAGE_KEY || evento.key === null) aoMudar();
  };
  window.addEventListener("storage", aoStorage);
  window.addEventListener(FOLLOWZAP_SHELL_CHANGE_EVENT, aoMudar);
  return () => {
    window.removeEventListener("storage", aoStorage);
    window.removeEventListener(FOLLOWZAP_SHELL_CHANGE_EVENT, aoMudar);
  };
}

export function salvarPreferenciaShellFollowZap(preferencia: PreferenciaShellFollowZap | null) {
  try {
    if (preferencia) window.localStorage.setItem(FOLLOWZAP_SHELL_STORAGE_KEY, preferencia);
    else window.localStorage.removeItem(FOLLOWZAP_SHELL_STORAGE_KEY);
  } catch {
    return;
  }
  window.dispatchEvent(new Event(FOLLOWZAP_SHELL_CHANGE_EVENT));
}

function ehDesktopFollowZap() {
  return window.matchMedia("(min-width: 1024px)").matches;
}

function desktopNoServidor() {
  return false;
}

function assinarViewportDesktop(aoMudar: () => void) {
  const media = window.matchMedia("(min-width: 1024px)");
  media.addEventListener("change", aoMudar);
  return () => media.removeEventListener("change", aoMudar);
}

export function aplicarAlternanciaShellDaUrl() {
  const url = new URL(window.location.href);
  const comando = url.searchParams.get(FOLLOWZAP_SHELL_QUERY_KEY);
  if (comando !== "current" && comando !== "experimental" && comando !== "reset") return;

  salvarPreferenciaShellFollowZap(comando === "reset" ? null : comando);
  url.searchParams.delete(FOLLOWZAP_SHELL_QUERY_KEY);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export function podeAplicarComandoShellDaUrl(webBeta: boolean, produto?: string | null) {
  return webBeta || produto === "FOLLOW_PRO";
}

export function useEstadoShellFollowZap(webBeta: boolean, produto?: string | null) {
  const preferencia = useSyncExternalStore(
    assinarPreferenciaShellFollowZap,
    lerPreferenciaShellNoNavegador,
    preferenciaShellNoServidor,
  );
  const desktop = useSyncExternalStore(assinarViewportDesktop, ehDesktopFollowZap, desktopNoServidor);
  const temaEscuro = useSyncExternalStore(assinarTemaFollowZap, temaEscuroNoNavegador, temaEscuroNoServidor);

  useEffect(() => {
    if (podeAplicarComandoShellDaUrl(webBeta, produto)) aplicarAlternanciaShellDaUrl();
  }, [webBeta, produto]);

  return { preferencia, desktop, temaEscuro };
}
